<div class="card">
  <h2 class="card-title">Financial reports</h2>

  <div class="card-body">
    <div class="tabs">
      <button type="button" class="tab-btn" [class.active]="activeTab === 'allocations'"
        (click)="selectTab('allocations')">Allocations &amp; payments</button>
      <button type="button" class="tab-btn" [class.active]="activeTab === 'payments'"
        (click)="selectTab('payments')">Requires payment</button>
      <button type="button" class="tab-btn" [class.active]="activeTab === 'claims'"
        (click)="selectTab('claims')">Claims per scholarship</button>
    </div>

    <form class="toolbar"
      (ngSubmit)="activeTab === 'payments' ? loadRequirement() : activeTab === 'claims' ? loadClaimReports() : loadAllocations()">
      <label>
        Cycle
        <input type="text" name="cycle" [(ngModel)]="cycle" placeholder="e.g. 2026" />
      </label>

      <label>
        Scholarship
        <select name="scholarshipId" [(ngModel)]="scholarshipId">
          <option [ngValue]="null">All scholarships</option>
          @for (scholarship of scholarships(); track scholarship.id) {
            <option [ngValue]="scholarship.id">{{ scholarship.title }}</option>
          }
        </select>
      </label>

      @if (activeTab === 'payments') {
        <label>
          Month
          <select name="month" [(ngModel)]="month">
            @for (option of months; track option.value) {
              <option [ngValue]="option.value">{{ option.label }}</option>
            }
          </select>
        </label>

        <label>
          Year
          <input type="number" name="year" [(ngModel)]="year" />
        </label>
      }

      <div class="toolbar-actions">
        <button type="submit" class="btn btn-primary btn-sm"><i class="fas fa-sync"></i> Run report</button>
      </div>
    </form>

    @if (error()) {
      <div class="alert alert-warning"><i class="fas fa-exclamation-circle"></i> {{ error() }}</div>
    }
    @if (loading()) {
      <div class="empty-state"><i class="fas fa-spinner fa-spin"></i> Running report…</div>
    }

    <!-- Allocations and payments ------------------------------------------------ -->
    @if (!loading() && activeTab === 'allocations') {
      @if (allocations().length === 0) {
        <div class="empty-state">
          <i class="fas fa-chart-pie"></i>
          <h3>No awards to report on</h3>
          <p>Awards appear here once scholarships have been awarded.</p>
        </div>
      }

      @for (report of allocations(); track report.scholarshipId) {
        <div class="report-block">
          <h3>{{ report.scholarshipTitle }}</h3>
          <p class="report-caption">
            {{ report.awardCount }} award{{ report.awardCount === 1 ? '' : 's' }}
            ({{ report.activeAwardCount }} active, {{ report.completedAwardCount }} completed)
          </p>

          <div class="totals">
            <div class="total"><span class="total-label">Allocated</span><span class="total-value">{{ money(report.totalAllocated, report.currency) }}</span></div>
            <div class="total"><span class="total-label">Used</span><span class="total-value">{{ money(report.totalUsed, report.currency) }}</span></div>
            <div class="total"><span class="total-label">Remaining</span><span class="total-value">{{ money(report.totalRemaining, report.currency) }}</span></div>
            <div class="total"><span class="total-label">Approved, unpaid</span><span class="total-value">{{ money(report.totalApprovedUnpaid, report.currency) }}</span></div>
            <div class="total"><span class="total-label">Scheduled payments</span><span class="total-value">{{ money(report.totalScheduledPayments, report.currency) }}</span></div>
          </div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Award</th>
                  <th>Cycle</th>
                  <th>Status</th>
                  <th>Allocated</th>
                  <th>Used</th>
                  <th>Remaining</th>
                  <th>Approved unpaid</th>
                  <th>Scheduled</th>
                  <th>Months left</th>
                </tr>
              </thead>
              <tbody>
                @for (award of report.awards; track award.scholarshipAwardId) {
                  <tr>
                    <td>
                      {{ award.studentName || '—' }}
                      @if (award.studentEmail) { <small>{{ award.studentEmail }}</small> }
                    </td>
                    <td>{{ award.awardReferenceNumber }}</td>
                    <td>{{ award.cycle }}</td>
                    <td>{{ award.status }}</td>
                    <td>{{ money(award.totalAllocation, report.currency) }}</td>
                    <td>{{ money(award.usedAmount, report.currency) }}</td>
                    <td>{{ money(award.remainingAmount, report.currency) }}</td>
                    <td>{{ money(award.approvedUnpaid, report.currency) }}</td>
                    <td>{{ money(award.scheduledPayments, report.currency) }}</td>
                    <td>{{ award.monthsRemaining }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }
    }

    <!-- Requires payment this month --------------------------------------------- -->
    @if (!loading() && activeTab === 'payments' && requirement(); as report) {
      <div class="totals">
        <div class="total"><span class="total-label">Total required</span><span class="total-value">{{ money(report.totalRequired, report.currency) }}</span></div>
        <div class="total"><span class="total-label">Payments due</span><span class="total-value">{{ report.paymentCount }}</span></div>
        <div class="total"><span class="total-label">Overdue</span><span class="total-value">{{ money(report.overdueAmount, report.currency) }} ({{ report.overdueCount }})</span></div>
        <div class="total"><span class="total-label">Approved claims</span><span class="total-value">{{ money(report.approvedClaimAmount, report.currency) }} ({{ report.approvedClaimCount }})</span></div>
      </div>

      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Reference</th>
              <th>Student</th>
              <th>Scholarship</th>
              <th>Benefit</th>
              <th>Payee</th>
              <th>Due</th>
              <th>Amount</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            @if (report.payments.length === 0) {
              <tr><td colspan="8" class="empty-cell">Nothing requires payment for this period.</td></tr>
            }
            @for (payment of report.payments; track payment.id) {
              <tr>
                <td>{{ payment.referenceNumber }}</td>
                <td>{{ payment.student.fullName || '—' }}</td>
                <td>{{ payment.scholarshipTitle || '—' }}</td>
                <td>{{ payment.benefitName }}</td>
                <td>{{ payment.payeeName || '—' }}</td>
                <td>{{ payment.dueDate | date: 'mediumDate' }}</td>
                <td>{{ money(payment.amount, payment.currency) }}</td>
                <td><span class="status status-pending">{{ payment.status }}</span></td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }

    <!-- Claims per scholarship --------------------------------------------------- -->
    @if (!loading() && activeTab === 'claims') {
      @if (claimReports().length === 0) {
        <div class="empty-state">
          <i class="fas fa-file-invoice"></i>
          <h3>No claims to report on</h3>
          <p>Claims appear here once students submit them.</p>
        </div>
      }

      @for (report of claimReports(); track report.scholarshipId) {
        <div class="report-block">
          <h3>{{ report.scholarshipTitle }}</h3>
          <div class="totals">
            <div class="total"><span class="total-label">Claims</span><span class="total-value">{{ report.totalClaims }}</span></div>
            <div class="total"><span class="total-label">Awaiting review</span><span class="total-value">{{ report.submittedClaims }}</span></div>
            <div class="total"><span class="total-label">Approved</span><span class="total-value">{{ report.approvedClaims }}</span></div>
            <div class="total"><span class="total-label">Rejected</span><span class="total-value">{{ report.rejectedClaims }}</span></div>
            <div class="total"><span class="total-label">Paid</span><span class="total-value">{{ report.paidClaims }}</span></div>
            <div class="total"><span class="total-label">Claimed</span><span class="total-value">{{ money(report.totalClaimedAmount, report.currency) }}</span></div>
            <div class="total"><span class="total-label">Approved value</span><span class="total-value">{{ money(report.totalApprovedAmount, report.currency) }}</span></div>
            <div class="total"><span class="total-label">Paid value</span><span class="total-value">{{ money(report.totalPaidAmount, report.currency) }}</span></div>
          </div>

          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Student</th>
                  <th>Category</th>
                  <th>Amount</th>
                  <th>Submitted</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                @for (claim of report.claims; track claim.id) {
                  <tr>
                    <td>{{ claim.referenceNumber }}</td>
                    <td>{{ claim.student.fullName || '—' }}</td>
                    <td>{{ claim.benefitName }}</td>
                    <td>{{ money(claim.amount, claim.currency) }}</td>
                    <td>{{ claim.submittedDate | date: 'mediumDate' }}</td>
                    <td>{{ claim.status }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      }
    }
  </div>
</div>
